import { useCallback, useEffect, useRef, useState } from "react";
import useAuthBootstrap from "../../../auth/useAuthBootstrap";

/*
 * Shared read state for the Insights screens. The app has no query library;
 * this is the smallest thing that lets the Hub and a detail screen share one
 * response: the last successful response per key is kept for the session and
 * shown at once on the next mount, and every mount still re-reads the server
 * (Insights is computed on read, so a cached figure is only ever a
 * placeholder for the fresh one). Errors are never cached.
 *
 * A response belongs to the signed-in user who read it. The cache key starts
 * with that user's id, followed by every request input (resource, timeframe,
 * timezone), so one account can never be shown another account's figures,
 * even when a logout and a new sign-in happen without a page reload. The
 * cache is also emptied whenever the signed-in identity changes, and nothing
 * is cached while no user is signed in.
 */

const responses = new Map();
let cacheOwner = null;

export function resetInsightsCacheForTests() {
  responses.clear();
  cacheOwner = null;
}

export function insightsCacheKey(identity, parts) {
  return JSON.stringify([identity ?? null, ...parts]);
}

// The cache only ever holds the current identity's responses.
function claimCache(identity) {
  if (identity !== cacheOwner) {
    responses.clear();
    cacheOwner = identity;
  }
}

function initialState(key, cacheable) {
  return cacheable && responses.has(key)
    ? { status: "success", data: responses.get(key), error: null }
    : { status: "loading", data: null, error: null };
}

// `parts`: every input of the request besides identity, e.g.
// ["adherence", timeframe, timezone].
export default function useInsightsResource(parts, load) {
  const identity = useAuthBootstrap().user?.id ?? null;
  const cacheable = Boolean(identity);
  claimCache(identity);
  const key = insightsCacheKey(identity, parts);

  const [state, setState] = useState(() => ({ key, ...initialState(key, cacheable) }));
  const [attempt, setAttempt] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  // A key change (timeframe, timezone or identity switch) starts from that
  // key's own state, never from the previous key's data.
  const current = state.key === key ? state : { key, ...initialState(key, cacheable) };
  if (current !== state) {
    setState(current);
  }

  useEffect(() => {
    let active = true;
    Promise.resolve()
      .then(() => loadRef.current())
      .then(
        (data) => {
          if (cacheable && cacheOwner === identity) responses.set(key, data);
          if (active) setState({ key, status: "success", data, error: null });
        },
        (error) => {
          if (!active) return;
          // A failed refresh keeps the last good response of this same key
          // (same user, resource, timeframe and timezone) only.
          setState((previous) => (previous.key === key && previous.status === "success"
            ? previous
            : { key, status: "error", data: null, error }));
        }
      );
    return () => {
      active = false;
    };
  }, [key, attempt, cacheable, identity]);

  const retry = useCallback(() => {
    setState({ key, status: "loading", data: null, error: null });
    setAttempt((value) => value + 1);
  }, [key]);

  return { ...current, retry };
}
